import { getSupabaseClient } from '../utils/supabaseClient';

/**
 * Service for logging and retrieving timesheet edit history
 * Provides immutable audit trail for compliance and transparency
 */
export class TimesheetEditHistoryService {
  /**
   * Log a timesheet edit to the immutable history table
   * @param {UUID} timesheetId - The timesheet being edited
   * @param {Object} beforeValues - Values before the edit
   * @param {Object} afterValues - Values after the edit
   * @param {String} editReason - Reason for the edit (required for compliance)
   * @param {UUID} editedBy - User ID who made the edit
   * @param {UUID} companyId - Company ID for scoping
   * @returns {Promise<Object>} The created history record
   */
  static async logTimesheetEdit(
    timesheetId,
    beforeValues,
    afterValues,
    editReason,
    editedBy,
    companyId
  ) {
    const supabase = getSupabaseClient();

    try {
      const { data, error } = await supabase
        .from('timesheet_edit_history')
        .insert({
          timesheet_id: timesheetId,
          edited_by: editedBy,
          edit_reason: editReason,
          before_values: beforeValues,
          after_values: afterValues,
          company_id: companyId
        })
        .select();

      if (error) {
        console.error('Error logging timesheet edit:', error);
        throw error;
      }

      return data?.[0] || null;
    } catch (error) {
      console.error('Failed to log timesheet edit:', error);
      throw error;
    }
  }

  /**
   * Get edit history for a specific timesheet
   * @param {UUID} timesheetId - The timesheet ID
   * @returns {Promise<Array>} Array of edit history records
   */
  static async getTimesheetHistory(timesheetId) {
    const supabase = getSupabaseClient();

    try {
      const { data, error } = await supabase
        .from('timesheet_edit_history')
        .select(`
          id,
          edited_by,
          edit_reason,
          before_values,
          after_values,
          edited_at,
          users(first_name, last_name, email)
        `)
        .eq('timesheet_id', timesheetId)
        .order('edited_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching timesheet history:', error);
      throw error;
    }
  }

  /**
   * Get edit history for all timesheets in a company
   * @param {UUID} companyId - The company ID
   * @param {Object} options - Query options (limit, offset, dateRange)
   * @returns {Promise<Array>} Array of edit history records
   */
  static async getCompanyTimesheetHistory(companyId, options = {}) {
    const supabase = getSupabaseClient();
    const { limit = 100, offset = 0, startDate, endDate } = options;

    try {
      let query = supabase
        .from('timesheet_edit_history')
        .select(`
          id,
          timesheet_id,
          edited_by,
          edit_reason,
          edited_at,
          users(first_name, last_name),
          employee_timesheets(employee_id, date)
        `)
        .eq('company_id', companyId)
        .order('edited_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (startDate) {
        query = query.gte('edited_at', startDate);
      }
      if (endDate) {
        query = query.lte('edited_at', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching company timesheet history:', error);
      throw error;
    }
  }

  /**
   * Check if a timesheet is locked (post-payroll)
   * @param {UUID} timesheetId - The timesheet ID
   * @returns {Promise<Boolean>} True if locked
   */
  static async isTimesheetLocked(timesheetId) {
    const supabase = getSupabaseClient();

    try {
      const { data, error } = await supabase
        .from('employee_timesheets')
        .select('locked_at')
        .eq('id', timesheetId)
        .single();

      if (error) throw error;
      return data?.locked_at !== null;
    } catch (error) {
      console.error('Error checking timesheet lock status:', error);
      return false;
    }
  }
}

export default TimesheetEditHistoryService;

